import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Returns the maximum number of interview retakes allowed by the org's plan.
 * - `null` means unlimited (no limit configured)
 * - `0` means no retakes allowed (only 1 attempt)
 * - `N` means N additional attempts allowed (total = N + 1)
 */
export function useInterviewRetakeLimit() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const { data, isLoading } = useQuery({
    queryKey: ['interview-retake-limit', orgId],
    queryFn: async () => {
      // Check global service kill switch first
      const { data: svcRow } = await supabase
        .from('services')
        .select('id, is_active')
        .eq('service_key', 'interview_retakes')
        .maybeSingle();

      // If service is globally disabled, no retakes allowed
      if (svcRow && !svcRow.is_active) return { maxRetakes: 0 };

      if (!svcRow) return { maxRetakes: 0 };

      // Get the org's active subscription plan
      const { data: sub } = await supabase
        .from('organization_subscriptions')
        .select('plan_id')
        .eq('org_id', orgId!)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!sub?.plan_id) return { maxRetakes: 0 };

      // Get plan-service mapping
      const { data: ps } = await supabase
        .from('plan_services')
        .select('is_enabled, usage_limit')
        .eq('plan_id', sub.plan_id)
        .eq('service_id', svcRow.id)
        .maybeSingle();

      if (!ps || !ps.is_enabled) return { maxRetakes: 0 }; // disabled = no retakes
      return { maxRetakes: ps.usage_limit }; // null = unlimited, N = max retakes
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    /** Max retakes allowed. null = unlimited, 0 = none, N = N retakes */
    maxRetakes: data?.maxRetakes ?? 0,
    isLoading,
  };
}
